import type { EventEmitter, TokenStorage, Tokens } from "../types";
import { BroadcastBus } from "../utils/BroadcastBus";

export interface SessionManagerOptions {
  storage: TokenStorage;
  events: EventEmitter;
  /** Actually perform a token refresh against the backend. Provided by the AuthProvider. */
  performRefresh: () => Promise<Tokens>;
  /** Actually perform a backend logout call (best-effort). Provided by the AuthProvider. */
  performLogout?: () => Promise<void>;
  /** Unique per host app, so multiple @ferrumec/auth instances on one origin don't collide. */
  channelName?: string;
  /** Refresh this many ms before expiry, instead of waiting for a 401. */
  refreshSkewMs?: number;
  /** Log the user out after this many ms of no user activity. Omit to disable. */
  idleTimeoutMs?: number;
}

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"] as const;

/**
 * Owns the token lifecycle: storage, scheduled refresh, single-flight
 * deduplication of concurrent refresh attempts, idle timeout, and
 * synchronization of logout/refresh across browser tabs.
 */
export class SessionManager {
  private readonly storage: TokenStorage;
  private readonly events: EventEmitter;
  private readonly performRefresh: () => Promise<Tokens>;
  private readonly performLogout?: () => Promise<void>;
  private readonly bus: BroadcastBus;
  private readonly refreshSkewMs: number;
  private readonly idleTimeoutMs?: number;

  private inFlightRefresh: Promise<Tokens> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private activityCleanup: (() => void) | null = null;
  private busUnsubscribe: (() => void) | null = null;

  constructor(opts: SessionManagerOptions) {
    this.storage = opts.storage;
    this.events = opts.events;
    this.performRefresh = opts.performRefresh;
    this.performLogout = opts.performLogout;
    this.refreshSkewMs = opts.refreshSkewMs ?? 30_000;
    this.idleTimeoutMs = opts.idleTimeoutMs;
    this.bus = new BroadcastBus(opts.channelName ?? "ferrumec-auth");

    this.busUnsubscribe = this.bus.subscribe((message) => {
      if (message.type === "logout") {
        this.handleRemoteLogout(message.reason);
      } else if (message.type === "tokensUpdated") {
        this.scheduleRefreshFromStorage();
      }
    });
  }

  getTokens(): Tokens | null {
    return this.storage.load();
  }

  setTokens(tokens: Tokens, opts: { broadcast?: boolean } = {}): void {
    this.storage.save(tokens);
    this.scheduleRefresh(tokens);
    if (opts.broadcast !== false) this.bus.post({ type: "tokensUpdated" });
  }

  clearTokens(): void {
    this.storage.clear();
    this.clearRefreshTimer();
  }

  /**
   * Returns a valid, non-expired access token, refreshing exactly once even
   * if many callers request it concurrently (single-flight deduplication).
   */
  async ensureFreshAccessToken(): Promise<string | null> {
    const tokens = this.storage.load();
    if (!tokens) return null;

    const isExpiring = tokens.expiresAt !== undefined && tokens.expiresAt - Date.now() <= this.refreshSkewMs;
    if (!isExpiring) return tokens.accessToken;

    return (await this.refreshOnce()).accessToken;
  }

  /** Force a refresh, deduplicated against any refresh already in flight. */
  refreshOnce(): Promise<Tokens> {
    if (this.inFlightRefresh) return this.inFlightRefresh;

    this.inFlightRefresh = this.performRefresh()
      .then((tokens) => {
        this.setTokens(tokens);
        this.events.emit("refresh", { tokens });
        return tokens;
      })
      .catch((err) => {
        this.clearTokens();
        this.events.emit("sessionExpired", undefined);
        throw err;
      })
      .finally(() => {
        this.inFlightRefresh = null;
      });

    return this.inFlightRefresh;
  }

  /** Log out locally, best-effort on the backend, and notify every other open tab. */
  async logout(reason: "user" | "sessionExpired" | "idle" = "user"): Promise<void> {
    this.stopIdleWatch();
    this.clearTokens();
    this.bus.post({ type: "logout", reason });
    this.events.emit("logout", { reason });
    if (this.performLogout) {
      try {
        await this.performLogout();
      } catch {
        // Best-effort: local session is already cleared regardless of backend outcome.
      }
    }
  }

  /** Start watching for idle timeout. Any listener from a previous call is torn down first. */
  startIdleWatch(): void {
    if (!this.idleTimeoutMs || typeof window === "undefined") return;
    this.stopIdleWatch();

    const reset = () => {
      if (this.idleTimer) clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => {
        void this.logout("idle");
      }, this.idleTimeoutMs);
    };

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, reset, { passive: true }));
    this.activityCleanup = () => ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, reset));
    reset();
  }

  stopIdleWatch(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = null;
    this.activityCleanup?.();
    this.activityCleanup = null;
  }

  /** Call once on unmount to release timers, listeners, and the broadcast channel. */
  dispose(): void {
    this.clearRefreshTimer();
    this.stopIdleWatch();
    this.busUnsubscribe?.();
    this.bus.close();
  }

  private scheduleRefreshFromStorage(): void {
    const tokens = this.storage.load();
    if (tokens) this.scheduleRefresh(tokens);
    else this.clearRefreshTimer();
  }

  private scheduleRefresh(tokens: Tokens): void {
    this.clearRefreshTimer();
    if (tokens.expiresAt === undefined) return;

    const delay = Math.max(tokens.expiresAt - Date.now() - this.refreshSkewMs, 0);
    this.refreshTimer = setTimeout(() => {
      this.refreshOnce().catch(() => {
        /* sessionExpired already emitted inside refreshOnce */
      });
    }, delay);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  private handleRemoteLogout(reason?: "user" | "sessionExpired" | "idle"): void {
    this.clearRefreshTimer();
    this.stopIdleWatch();
    this.events.emit("logout", { reason });
  }
}
